import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../useRenderManager";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { navigateToCitrixXenApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { TileRenderState } from "../RenderState/common";
import { AppType } from "client/Api/Apps/common";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "./useInvokeNavigation";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type CitrixXenAppTileContentProps = {
	renderState: TileRenderState<AppType.CitrixXenApp>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const CitrixXenAppTileContent = (props: CitrixXenAppTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useCitrixXenAppTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
CitrixXenAppTileContent.displayName = "CitrixXenAppTileContent";

export function useCitrixXenAppTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.CitrixXenApp>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		conditionalAccess,
		maintenanceWindow
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					(): void => {
						registerTileUsage();

						navigateToCitrixXenApp({ id: environmentAppId });
					},
					[environmentAppId, registerTileUsage]
				)
			)
		)
	);
}
